// /app/frontend/src/components/handshake/ClientHandshakeQR.js
// QR Code de segurança do cliente para verificação na entrega
import React, { useState, useEffect, useCallback } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { 
  FiRefreshCw, FiCopy, FiCheck, FiClock, FiShield,
  FiAlertTriangle, FiCamera
} from 'react-icons/fi';
import api from '@/services/api';

const ClientHandshakeQR = ({ orderId, orderStatus }) => {
  const [handshake, setHandshake] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);
  const [error, setError] = useState('');
  const [showScanner, setShowScanner] = useState(false);
  const [productCode, setProductCode] = useState('');
  const [verifyError, setVerifyError] = useState('');
  const [verifying, setVerifying] = useState(false);

  const loadHandshake = useCallback(async () => {
    try {
      const response = await api.get(`/orders/${orderId}/handshake`);
      setHandshake(response.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao carregar dados de verificação');
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    loadHandshake();
    const interval = setInterval(loadHandshake, 30000); // Atualiza a cada 30s
    return () => clearInterval(interval);
  }, [loadHandshake]);

  // Contador de tempo
  useEffect(() => {
    if (!handshake?.qr_expiry_minutes) return;
    
    const checkExpiry = () => {
      const expired = handshake.client_qr_expired;
      if (expired) {
        setTimeLeft(0);
      } else {
        setTimeLeft(handshake.qr_expiry_minutes * 60);
      }
    };
    
    checkExpiry();
    const interval = setInterval(() => {
      setTimeLeft(prev => prev > 0 ? prev - 1 : 0);
    }, 1000);
    
    return () => clearInterval(interval);
  }, [handshake]);

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      await api.post(`/orders/${orderId}/handshake/regenerate-qr`, {
        order_id: orderId,
        qr_type: 'client'
      });
      await loadHandshake();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao regenerar QR');
    } finally {
      setRegenerating(false);
    }
  };

  const copyPin = async () => {
    try {
      await navigator.clipboard.writeText(handshake.backup_pin);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Erro ao copiar:', err);
    }
  };

  const handleVerifyProduct = async () => {
    if (!productCode.trim()) {
      setVerifyError('Digite o código do produto');
      return;
    }

    setVerifying(true);
    setVerifyError('');

    try {
      await api.post('/delivery/dropoff/verify-product-by-client', {
        order_id: orderId,
        product_qr_token: productCode.trim(),
        method: productCode.length === 6 ? 'pin' : 'qr'
      });
      
      // Recarregar dados
      await loadHandshake();
      setShowScanner(false);
      setProductCode('');
    } catch (err) {
      setVerifyError(err.response?.data?.detail || 'Código inválido');
    } finally {
      setVerifying(false);
    }
  };

  const formatTime = (seconds) => {
    if (seconds <= 0) return 'Expirado';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Não mostrar se pedido já foi entregue
  if (orderStatus === 'entregue') {
    return null;
  }

  // Só mostrar quando o pedido estiver em rota ou pronto para coleta
  if (!['em_rota', 'coleta'].includes(orderStatus)) {
    return null;
  }

  if (loading) {
    return (
      <div className="bg-white rounded-xl p-4 shadow-sm">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent"></div>
        </div>
      </div>
    );
  }

  if (error && !handshake) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-4">
        <p className="text-red-700 flex items-center gap-2">
          <FiAlertTriangle />
          {error}
        </p>
        <button
          onClick={loadHandshake}
          className="mt-2 text-sm text-red-600 hover:text-red-800"
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="client-handshake-qr">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-500 to-green-600 p-4">
        <h3 className="text-white font-bold flex items-center gap-2">
          <FiShield />
          Verificação de Segurança
        </h3>
        <p className="text-green-100 text-sm">
          Mostre este código ao entregador
        </p>
      </div>

      <div className="p-4 space-y-4">
        {/* Instruções */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <h4 className="font-medium text-blue-800 mb-2">📱 Como funciona:</h4>
          <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
            <li>O entregador chega e escaneia seu QR Code</li>
            <li>Você escaneia/digita o código do produto</li>
            <li>Entrega confirmada com segurança!</li>
          </ol>
        </div>

        {/* QR Code do Cliente */}
        <div className="text-center">
          {handshake?.client_qr_expired ? (
            <div className="py-8 bg-yellow-50 rounded-xl">
              <FiAlertTriangle className="mx-auto text-4xl text-yellow-500 mb-2" />
              <p className="text-gray-600 mb-2">QR Code expirado</p>
              <button
                onClick={handleRegenerate}
                disabled={regenerating}
                className="px-6 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 disabled:opacity-50 flex items-center gap-2 mx-auto"
              >
                <FiRefreshCw className={regenerating ? 'animate-spin' : ''} />
                {regenerating ? 'Gerando...' : 'Gerar Novo QR'}
              </button>
            </div>
          ) : (
            <div className="bg-white border-2 border-dashed border-gray-300 rounded-xl p-6">
              <QRCodeSVG
                value={handshake?.client_qr_token || 'loading'}
                size={180}
                level="H"
                includeMargin
                className="mx-auto"
              />
              <div className="mt-4 flex items-center justify-center gap-2 text-sm text-gray-500">
                <FiClock />
                <span>Expira em: {formatTime(timeLeft)}</span>
              </div>
            </div>
          )}
        </div>

        {/* PIN de Backup */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
          <p className="text-sm text-yellow-700 mb-2 font-medium">
            🔐 PIN de Backup (se o scanner não funcionar):
          </p>
          <div className="flex items-center justify-center gap-3">
            <span className="text-3xl font-mono font-bold tracking-[0.3em] text-gray-800">
              {handshake?.backup_pin || '------'}
            </span>
            <button
              onClick={copyPin}
              className="p-2 text-gray-500 hover:text-gray-700 transition-colors"
            >
              {copied ? <FiCheck className="text-green-500" /> : <FiCopy />}
            </button>
          </div>
          {copied && (
            <p className="text-green-600 text-sm text-center mt-1">✓ Copiado!</p>
          )}
        </div>

        {/* Status de Verificação */}
        <div className="bg-gray-50 rounded-xl p-4">
          <p className="text-sm font-medium text-gray-700 mb-3">Status da Verificação:</p>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className={`w-3 h-3 rounded-full ${
                handshake?.dropoff_arrived ? 'bg-green-500' : 'bg-gray-300'
              }`}></span>
              <span className={`text-sm ${
                handshake?.dropoff_arrived ? 'text-green-700' : 'text-gray-500'
              }`}>
                Entregador chegou no endereço
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className={`w-3 h-3 rounded-full ${
                handshake?.dropoff_client_verified ? 'bg-green-500' : 'bg-gray-300'
              }`}></span>
              <span className={`text-sm ${
                handshake?.dropoff_client_verified ? 'text-green-700' : 'text-gray-500'
              }`}>
                Seu QR foi verificado
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className={`w-3 h-3 rounded-full ${
                handshake?.dropoff_product_verified ? 'bg-green-500' : 'bg-gray-300'
              }`}></span>
              <span className={`text-sm ${
                handshake?.dropoff_product_verified ? 'text-green-700' : 'text-gray-500'
              }`}>
                Você confirmou o produto
              </span>
            </div>
          </div>
        </div>

        {/* Botão para verificar produto (aparece após cliente ser verificado) */}
        {handshake?.dropoff_client_verified && !handshake?.dropoff_product_verified && (
          <div className="border-t pt-4">
            {showScanner ? (
              <div className="space-y-3">
                <p className="text-sm text-gray-600 text-center">
                  Digite o código do produto que o entregador vai mostrar:
                </p>
                <input
                  type="text"
                  value={productCode}
                  onChange={(e) => setProductCode(e.target.value.toUpperCase())}
                  placeholder="Código do produto ou PIN"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-center text-xl font-mono tracking-widest focus:border-green-500 focus:outline-none"
                  maxLength={50}
                />
                {verifyError && (
                  <p className="text-red-500 text-sm text-center">{verifyError}</p>
                )}
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowScanner(false)}
                    className="flex-1 py-3 bg-gray-200 text-gray-700 rounded-xl"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleVerifyProduct}
                    disabled={verifying || !productCode.trim()}
                    className="flex-1 py-3 bg-green-500 text-white rounded-xl disabled:opacity-50"
                  >
                    {verifying ? 'Verificando...' : 'Confirmar'}
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowScanner(true)}
                className="w-full py-4 bg-green-500 text-white rounded-xl font-bold flex items-center justify-center gap-2"
              >
                <FiCamera />
                Confirmar Recebimento do Produto
              </button>
            )}
          </div>
        )}

        {/* Mensagem de sucesso */}
        {handshake?.dropoff_product_verified && (
          <div className="bg-green-100 border border-green-300 rounded-xl p-4 text-center">
            <FiCheck className="mx-auto text-4xl text-green-500 mb-2" />
            <p className="text-green-800 font-bold">Entrega Verificada!</p>
            <p className="text-green-600 text-sm">
              Aguarde o entregador finalizar a entrega com a foto.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientHandshakeQR;
